import {
  AlarmStatus,
  EdgeDeviceOperationalStatus,
  EdgeDeviceOperationalStatusStatus,
  EquipmentCategory,
  EquipmentUnitInstallStatus,
} from './global';

/* ***************************************** ENUMS ********************************************** */

export enum CrusherAutomationType {
  disabled = 'disabled',
  powerControl = 'powerControl',
}

export enum CrusherDriveMotorType {
  starter = 'starter',
  vfd = 'vfd',
}

export enum CrusherVSIDriveType {
  beltDrive = 'beltDrive',
  gearDrive = 'gearDrive',
}

export enum CrusherMetalDetectorStatus {
  clear = 'clear',
  detected = 'detected',
}

export enum CrusherFeederAutomationMode {
  manual = 'manual',
  auto = 'auto',
}

export enum CrusherMotorControl {
  manual = 'manual',
  auto = 'auto',
}

export enum CrusherOperationMode {
  local = 'local',
  remote = 'remote',
  service = 'service',
}

export enum CrusherOverallStatus {
  good = 'good',
  warning = 'warning',
  alarm = 'alarm',
}

export enum LubricationFlowStatus {
  good = 'good',
  low = 'low',
}

export enum CrusherRunningSStatus {
  starting = 'starting',
  running = 'running',
  fault = 'fault',
  off = 'off',
}

export enum CrusherRunningEStatus {
  enabled = 'enabled',
  fault = 'fault',
  off = 'off',
  running = 'running',
}

export enum CrusherWeatherModeStatus {
  on = 'on',
  off = 'off',
}

export enum CrusherDashboardChartRange {
  live = 'live',
  lastEvent = 'lastEvent',
}

export enum ExportReportType {
  mainDashboard = 'mainDashboard',
}

export enum ExportPeriodPreset {
  oneDay = '1d',
  threeDays = '3d',
  sevenDays = '7d',
}

export enum CrusherWebsocketRequestEvents {
  realTimeCrusherData = 'realTimeCrusherData',
}

export enum CrusherWebsocketResponseEvents {
  equipmentUnitCrusherData = 'equipmentUnitCrusherData',
}

export enum CrusherModel {
  p200 = 'p200',
  p300 = 'p300',
  p400 = 'p400',
  p500 = 'p500',
  p600 = 'p600',
  d350 = 'd350',
  vsi = 'vsi',
}

export enum CrusherPLCTagSchema {
  // Tag Schema Patriot
  t1patriot = 't1patriot',

  // Tag Schema VSI
  t1vsi = 't1vsi',
}

export enum CrusherDataSchema {
  // Data Schema Patriot
  d1patriot = 'd1patriot',

  // Data Schema VSI
  d1vsi = 'd1vsi',
}

export enum EquipmentDataReadStatus {
  valid = 'valid',
  invalid = 'invalid',
}

export enum SolenoidStatus {
  success = 'success',
  fault = 'fault',
  disabled = 'disabled',
  disconnected = 'disconnected',
}

/* **************************************** SCHEMA TYPES **************************************** */

// Schema Configuration Types - Discriminated Union for Type Safety
export type SchemaConfig =
  | {
    plcTagSchema: CrusherPLCTagSchema.t1patriot;
    dataSchema: CrusherDataSchema.d1patriot;
  } // Patriot/Dakota
  | {
    plcTagSchema: CrusherPLCTagSchema.t1vsi;
    dataSchema: CrusherDataSchema.d1vsi;
  }; // VSI

// Equipment with Schema - Extended type for type-safe schema operations
export type EquipmentWithSchema = EquipmentUnitCrusher & SchemaConfig;

// Schema Type Union
export enum SchemaType {
  patriot = 'patriot',
  vsi = 'vsi',
  unknown = 'unknown',
}

/* **************************************** TRENDINGS ******************************************* */

export enum TrendingType {
  motorCurrent = 'motorCurrent',
  pid = 'pid',
  lubeUnit = 'lubeUnit',
  hydraulic = 'hydraulic',
  ringBounceSensor = 'ringBounceSensor',
  coastDown = 'coastDown',
  custom = 'custom',
}

/* *************************************** INTERFACES ******************************************* */

export interface CrusherSchemas {
  [CrusherModel.p200]: Record<CrusherPLCTagSchema.t1patriot, CrusherDataSchema.d1patriot[]>;
  [CrusherModel.p300]: Record<CrusherPLCTagSchema.t1patriot, CrusherDataSchema.d1patriot[]>;
  [CrusherModel.p400]: Record<CrusherPLCTagSchema.t1patriot, CrusherDataSchema.d1patriot[]>;
  [CrusherModel.p500]: Record<CrusherPLCTagSchema.t1patriot, CrusherDataSchema.d1patriot[]>;
  [CrusherModel.p600]: Record<CrusherPLCTagSchema.t1patriot, CrusherDataSchema.d1patriot[]>;

  // Dakota
  [CrusherModel.d350]: Record<CrusherPLCTagSchema.t1patriot, CrusherDataSchema.d1patriot[]>;

  // VSI
  [CrusherModel.vsi]: Record<CrusherPLCTagSchema.t1vsi, CrusherDataSchema.d1vsi[]>;
}

export interface EquipmentUnitCrusher {
  createdAt: number;
  createdBy: string;
  customerUnitName: string;
  dataSchema: CrusherDataSchema;
  deletedAt?: number | null;
  deletedBy?: string | null;
  edgeStatus: EdgeDeviceOperationalStatusStatus | null;
  equipmentCategory: EquipmentCategory;
  lastHeartbeatTimestamp: number | null;
  gpsEnabled: boolean;
  id: string;
  idArea?: string | null;
  idSite: string;
  installedAt: number;
  installedBy: string;
  installStatus: EquipmentUnitInstallStatus;
  lat?: number | null;
  lng?: number | null;
  model: CrusherModel;
  plcTagSchema: CrusherPLCTagSchema;
  serialNumber: string;
  updatedAt?: number | null;
  updatedBy?: string | null;
}

export interface EquipmentUnitDataConeCrusher {
  // *** EquipmentUnitData *** //
  idEquipmentUnit: string;
  equipmentCategory: EquipmentCategory;
  timestamp: number | null;
  readStatus: EquipmentDataReadStatus;
  unitStatus: CrusherRunningSStatus | null;

  // *** GPS Data *** //
  lat?: number | null;
  lng?: number | null;
  gpsTimestamp?: number | null;

  // *** ASI Network Status *** //
  ifmAdditionalNode: boolean | null;
  ifmInvalidProfile: boolean | null;
  ifmMissingNode: boolean | null;
  ifmEarthFault: boolean | null;
  ifmGatewayInactive: boolean | null;
  ifmInternalError: boolean | null;
  ifmTempLimit: boolean | null;
  ifm19AsiPowerFail: boolean | null;
  ifm225PowerFail: boolean | null;
  ifmAsiVoltageFE: number | null;
  ifmAsiVoltageVDC: number | null;
  ifmAsiSymmetry: number | null;

  // *** ASI Network Check *** //
  ifmNetworkNotProjected1A: boolean | null;
  ifmDuplicateAddress1A: boolean | null;
  ifmNetworkPeriphery1A: boolean | null;
  ifmMissingAddress1A: boolean | null;
  ifmNetworkProfile1A: boolean | null;

  ifmNetworkNotProjected1B: boolean | null;
  ifmDuplicateAddress1B: boolean | null;
  ifmNetworkPeriphery1B: boolean | null;
  ifmMissingAddress1B: boolean | null;
  ifmNetworkProfile1B: boolean | null;

  ifmNetworkNotProjected2: boolean | null;
  ifmDuplicateAddress2: boolean | null;
  ifmNetworkPeriphery2: boolean | null;
  ifmMissingAddress2: boolean | null;
  ifmNetworkProfile2: boolean | null;

  ifmNetworkNotProjected7: boolean | null;
  ifmDuplicateAddress7: boolean | null;
  ifmNetworkPeriphery7: boolean | null;
  ifmMissingAddress7: boolean | null;
  ifmNetworkProfile7: boolean | null;

  ifmNetworkNotProjected8: boolean | null;
  ifmDuplicateAddress8: boolean | null;
  ifmNetworkPeriphery8: boolean | null;
  ifmMissingAddress8: boolean | null;
  ifmNetworkProfile8: boolean | null;

  ifmNetworkNotProjected9: boolean | null;
  ifmDuplicateAddress9: boolean | null;
  ifmNetworkPeriphery9: boolean | null;
  ifmMissingAddress9: boolean | null;
  ifmNetworkProfile9: boolean | null;

  ifmNetworkNotProjected10: boolean | null;
  ifmDuplicateAddress10: boolean | null;
  ifmNetworkPeriphery10: boolean | null;
  ifmMissingAddress10: boolean | null;
  ifmNetworkProfile10: boolean | null;

  ifmNetworkNotProjected11: boolean | null;
  ifmDuplicateAddress11: boolean | null;
  ifmNetworkPeriphery11: boolean | null;
  ifmMissingAddress11: boolean | null;
  ifmNetworkProfile11: boolean | null;

  ifmNetworkNotProjected12: boolean | null;
  ifmDuplicateAddress12: boolean | null;
  ifmNetworkPeriphery12: boolean | null;
  ifmMissingAddress12: boolean | null;
  ifmNetworkProfile12: boolean | null;

  ifmNetworkNotProjected13: boolean | null;
  ifmDuplicateAddress13: boolean | null;
  ifmNetworkPeriphery13: boolean | null;
  ifmMissingAddress13: boolean | null;
  ifmNetworkProfile13: boolean | null;

  ifmNetworkNotProjected14: boolean | null;
  ifmDuplicateAddress14: boolean | null;
  ifmNetworkPeriphery14: boolean | null;
  ifmMissingAddress14: boolean | null;
  ifmNetworkProfile14: boolean | null;

  ifmNetworkNotProjected15: boolean | null;
  ifmDuplicateAddress15: boolean | null;
  ifmNetworkPeriphery15: boolean | null;
  ifmMissingAddress15: boolean | null;
  ifmNetworkProfile15: boolean | null;

  // *** IFM Module Status *** //
  // ADR 1A
  dInputCrLinerContact: boolean | null;
  dInputHydClampPressurize: boolean | null;
  dInputHydTrampPressurize: boolean | null;
  dOutputHydSol7: boolean | null;
  dOutputHydSol6: boolean | null;
  dOutputHydSol2: boolean | null;
  dOutputHydSol1: boolean | null;

  // ADR 1B
  dInputHydTemp: boolean | null;
  dInputHydLevelLow: boolean | null;
  dOutputHydSol8: boolean | null;
  dOutputHydSol5: boolean | null;
  dOutputHydSol4: boolean | null;
  dOutputHydSol3: boolean | null;

  // ADR 2
  analogHydClampPressure: number | null;
  analogHydTrampPressure: number | null;

  // ADR 7
  analogLubeTankTemp: number | null;
  analogLubeReturnTemp: number | null;

  // ADR 8
  analogLubeSupplyTemp: number | null;
  analogLubeFlow: number | null;

  // ADR 9
  dInputLubeFilterDirty: boolean | null;
  dInputLubeFilterPlugged: boolean | null;
  dInputLubeFlowSwitch: boolean | null;
  dInputLubeTankLevel: boolean | null;

  // ADR 10
  analogVibration4: number | null;
  analogCrEccentricTemp: number | null;
  analogLubeBrakePressure: number | null;

  // ADR 11
  analogLubeOilPressure: number | null;
  analogVibration1: number | null;

  // ADR 12
  analogVibration2: number | null;
  analogVibration3: number | null;

  // ADR 13A
  dInputCRAdjProx1: boolean | null;
  dInputCRAdjProx2: boolean | null;

  // ADR 14
  analogCrusherSpeed: number | null;
  analogCountershaftTemp: number | null;

  // ADR 15
  analogCavityLevel: number | null;

  // *** PLC 24 VDC Inputs *** //
  dInputTrampReset: boolean | null;
  dInputTrampClear: boolean | null;
  dInputCssOpen: boolean | null;
  dInputCssClose: boolean | null;
  dInputHighSpeed: boolean | null;

  // *** PLC 24 VDC Outputs *** //
  dOutputRemoteBoxAlarm: boolean | null;
  dOutputRemoteCrusherEnable: boolean | null;
  dOutputRemoteBoxPower: boolean | null;

  // *** DC Input Card *** //
  dInputPlcControl: boolean | null;
  dInputCrMotor1Aux: boolean | null;
  dInputCrMotor2Aux: boolean | null;
  dInputLubePump1Aux: boolean | null;
  dInputLubePump2Aux: boolean | null;
  dInputLubeCooler1Aux: boolean | null;
  dInputLubeHeater1Aux: boolean | null;
  dInputLubeHeager2Aux: boolean | null;
  dInputHpuMotoAux: boolean | null;
  dInputDischargeConveyorAux: boolean | null;
  dInputCountershaftBlowerAux: boolean | null;
  dInputFeedDriveAux: boolean | null;
  dInputRemoteAutoStart: boolean | null;
  dInputRemoteAutoStop: boolean | null;
  dInputMotor1Run: boolean | null;
  dInputMotor2Run: boolean | null;
  dInputMotor1Fault: boolean | null;
  dInputMotor2Fault: boolean | null;
  dInputMotor1Ready: boolean | null;
  dInputMotor2Ready: boolean | null;

  // *** DC Output Card *** //
  dOutputMotor1Run: boolean | null;
  dOutputMotor2Run: boolean | null;
  dOutputLubePump1Run: boolean | null;
  dOutputLubePump2Run: boolean | null;
  dOutputLubeCooler1Run: boolean | null;
  dOutputLubeHeaterRun: boolean | null;
  dOutputLubeHeaterRun2: boolean | null;
  dOutputHpuMotorRun: boolean | null;
  dOutputDischargeConveyor: boolean | null;
  dOutputCounterShaftBlowerRun: boolean | null;
  dOutputFeedConveyorRun: boolean | null;
  dOutputCrusherPermissiveOk: boolean | null;
  dOutputLubePermissiveOk: boolean | null;
  dOutputFeedPermissiveOk: boolean | null;
  dOutputProgSequenceStart: boolean | null;
  dOutputWarningHorn: boolean | null;
  dOutputMotor1Reset: boolean | null;
  dOutputMotor2Reset: boolean | null;
  dOutputMotor1Forward: boolean | null;
  dOutputMotor2Forward: boolean | null;
  dOutputMotor1Reverse: boolean | null;
  dOutputMotor2Reverse: boolean | null;

  // *** PLC Analog Card *** //
  analogCrMotor1Amps: number | null;
  analogCrMotor2Amps: number | null;
  analogFeedSpeed: number | null;

  // *** EquipmentUnitDataConeCrusher *** //
  beltSlipPermissive: boolean | null;
  bowlCloseRequest: boolean | null;
  bowlMovementSensorProx1: boolean | null;
  bowlMovementSensorProx2: boolean | null;
  bowlOpenRequest: boolean | null;
  calibrationActive: boolean | null;
  cavityLevel: number | null;
  cavityLevelTransducerFault: boolean | null;
  clampPressureSensorFault: boolean | null;
  coolerFanStatus: CrusherRunningSStatus | null;
  countershaftBlowerRunningStatus: boolean | null;
  countershaftBushingTemp: number | null;
  countershaftBushingTransducerFault: boolean | null;
  countershaftEccentricTemp: number | null;
  crusherAboveMinimumCSS: boolean | null;
  crusherBeltSlip: boolean | null;
  crusherBowlCreep: boolean | null;
  crusherMotor: CrusherRunningSStatus | null;
  crusherMotor1Fault: boolean | null;
  crusherMotor1RunHours: number | null;
  crusherMotor2RunHours: number | null;
  crusherMotor2Fault: boolean | null;
  crusherMotorAmps: number | null;
  crusherMotor2Amps: number | null;
  crusherMotorOverallStatus: CrusherOverallStatus | null;
  crusherMotorRpm: number | null;
  crusherOperationMode: boolean | null;
  crusherStartPermissives: boolean | null;
  csSetting: number | null;
  currentLinerCalibrationCount: number | null;
  currentLinerHoursSinceLastCalibration: number | null;
  dischargeRunHours: number | null;
  dischargeRunningStatus: CrusherRunningSStatus | null;
  eStop: boolean | null;
  eccentrictBushingTransducerFault: boolean | null;
  feederAutomation: CrusherFeederAutomationMode | null;
  feederRunHours: number | null;
  feederRunningStatus: CrusherRunningSStatus | null;
  feederPermissives: boolean | null;
  feederStarterFault: boolean | null;
  highCavityLevel: boolean | null;
  highCounterShaftBushingTemp: boolean | null;
  highCrusherCurrentPermissive: boolean | null;
  highEccentricBushingTemp: boolean | null;
  highOilTemp: boolean | null;
  highVibrationPermissive: boolean | null;
  hydExcessiveClampPressure: boolean | null;
  hydExcessiveTrampPressure: boolean | null;
  hydLowTrampPressure: boolean | null;
  hydLowClampPressure: boolean | null;
  hydPumpRunningOver1Hour: boolean | null;
  hydraulicClampPressure: number | null;
  hydraulicOverallStatus: CrusherOverallStatus | null;
  hydraulicPermissive: boolean | null;
  hydraulicPumpRunningStatus: CrusherRunningSStatus | null;
  hydraulicTankTempStatus: CrusherOverallStatus | null;
  hydraulicTrampPressure: number | null;
  linerInstalledDateDay: number | null;
  linerInstalledDateMonth: number | null;
  linerInstalledDateYear: number | null;
  linerWear: number | null;
  lowLubeOil: boolean | null;
  linerWearRemainingPercent: number | null;
  lowLubeOilPressure: boolean | null;
  lubeDifferentialTempPermissive: boolean | null;
  analogLubeFlow1: number | null;
  analogLubeFlow2: number | null;
  lubeFlowScld: number | null;
  lubeFlowScld1: number | null;
  lubeFlowScld2: number | null;
  spLubeFlow1ScaleMax: number | null;
  spLubeFlow1ScaleMin: number | null;
  spLubeFlow2ScaleMax: number | null;
  spLubeFlow2ScaleMin: number | null;
  lubeHeaterPermissive: boolean | null;
  lubeHeaterStarterFault: boolean | null;
  lubeHighReturnTempPermissive: boolean | null;
  lubeOilLevelLow: boolean | null;
  lubeOilPressure: boolean | null;
  lubePermissive: boolean | null;
  lubePressureTransducerFault: boolean | null;
  lubePump1RunHours: number | null;
  lubePump1StarterFault: boolean | null;
  lubePumpRunningStatus: CrusherRunningSStatus | null;
  lubePumpStarterFault: boolean | null;
  lubeReturnLowTemp: boolean | null;
  lubeReturnTempHigh: boolean | null;
  lubeReturnTempTransducerFault: boolean | null;
  lubeSupplyDifferential: number | null;
  lubeTankHeaterEnable: boolean | null;
  lubeTankLevel: boolean | null;
  lubeTankOilLevel: boolean | null;
  lubeTankTempTransducerFault: boolean | null;
  lubricationOilPressure: number | null;
  lubricationOverallStatus: CrusherOverallStatus | null;
  lubricationReturnTemperature: number | null;
  lubricationSupplyTemperature: number | null;
  lubricationTankTemperature: number | null;
  minCrusherRunTimer: boolean | null;
  minimumLubeRunTimer: boolean | null;
  motorControl: CrusherMotorControl | null;
  numberOfTeeth: number | null;
  oilLevelLow: boolean | null;
  operationModeLocalRemote: CrusherOperationMode | null;
  operationModeOperateService: CrusherOperationMode | null;
  pumpOverPressure: boolean | null;
  spAmpAdjustHighAmpsEnable: boolean | null;
  spAmpCloseAlarmTeeth: number | null;
  spAmpCountFeedStoppage: number | null;
  spAmpOpenAlarmTeeth: number | null;
  spAmpShiftTimeHours: number | null;
  spAutoBowlAdjustLowAmpsEnable: boolean | null;
  spAutoBowlAdjustVibrationEnable: boolean | null;
  spAutoCloseMinCss: number | null;
  spAutoModeSelection: number | null;
  spBeltSlipAlarmRpm: number | null;
  spBowlAdjustmentMaxClampPressure: number | null;
  spBowlAdjustmentUnderLoadBool: boolean | null;
  spCavityLevelMaxLevel: number | null;
  spClampPressureLowAlarm: number | null;
  spClampPressureScaleMax: number | null;
  spClampPressureScaleMin: number | null;
  spClampPressureStartPressurize: number | null;
  spClampPressureStopPressurize: number | null;
  spCounterShaftBushingScaleMax: number | null;
  spCounterShaftBushingScaleMin: number | null;
  spCounterShaftTempWarning: number | null;
  spCrusherAdjustmentWarningHornTime: number | null;
  spCrusherMotorAmpsScaleMax: number | null;
  spCrusherMotorAmpsScaleMin: number | null;
  spCrusherMotorAutoAmps: number | null;
  spCrusherMotorAutoAmpsHighLimit: number | null;
  spCrusherMotorAutoAmpsLowLimit: number | null;
  spCrusherMotorCrushingAmps: number | null;
  spCrusherMotorFullLoadAmps: number | null;
  spCrusherMotorRpmScaleMax: number | null;
  spCrusherMotorRpmScaleMin: number | null;
  spCrusherMotorRunningAmps: number | null;
  spCrusherMotorServiceFactor: number | null;
  spCrusherMotorWarningHornTime: number | null;
  spDischargeWarningHornTime: number | null;
  spEccentricBushingScaleMax: number | null;
  spEccentricBushingScaleMin: number | null;
  spEccentricBushingTempHigh: number | null;
  spEccentricBushingTempHighStopCrusher: number | null;
  spEccentricBushingTempHighStopFeed: number | null;
  spFeedCavityLevel: number | null;
  spFeedCavityLevelHighLimit: number | null;
  spFeederWarningHornTime: number | null;
  spLowAmpsRespTime: number | null;
  spLubeCoolerStartTemp: number | null;
  spLubeCoolerStopTemp: number | null;
  spLubeHeaterStartTemp: number | null;
  spLubeHeaterStopTemp: number | null;
  spLubePressureLowAlarm: number | null;
  spLubePressureLowWarning: number | null;
  spLubePressureNormal: number | null;
  spLubePressureStopAlarm: number | null;
  spLubeReturnTempCrusherStartPermissive: number | null;
  spLubeReturnTempCrusherStop: number | null;
  spLubeReturnTempHighWarn: number | null;
  spLubeReturnTempLowAlarm: number | null;
  spLubeReturnTempLowWarning: number | null;
  spLubeReturnTempStopFeed: number | null;
  spLubeSupplyReturnTempDiffAlarm: number | null;
  spLubeSupplyReturnTempDiffDelay: number | null;
  spLubeSupplyTempLowWarn: number | null;
  spLubeTankTempHighAlarm: number | null;
  spLubeTankTempHighWarning: number | null;
  spLubeTankTempLowAlarm: number | null;
  spLubeTankTempLowWarning: number | null;
  spLubeFlowScaleMin: number | null;
  spLubeFlowScaleMax: number | null;
  antiSpinPressure: number | null;
  spMaxFeederSpeed: number | null;
  spPidAutoLevelFeederSpeedKd: number | null;
  spPidAutoLevelFeederSpeedKi: number | null;
  spPidAutoLevelFeederSpeedKp: number | null;
  spReturnTempScaleMax: number | null;
  spReturnTempScaleMin: number | null;
  spSupplyPressureScaleMax: number | null;
  spSupplyPressureScaleMin: number | null;
  spSupplyTempScaleMin: number | null;
  spSupplyTempScaleMax: number | null;
  spSystemPressureHighAlarm: number | null;
  spSystemPressureScaleMax: number | null;
  spSystemPressureScaleMin: number | null;
  spTankTempScaleMax: number | null;
  spTankTempScaleMin: number | null;
  spTimeAboveFlaFeedStop: number | null;
  spTimeAboveFlaSf115FeedStop: number | null;
  spTimeAboveFlaSfFeedStop: number | null;
  spTrampPressureLowAlarm: number | null;
  spTrampPressureScaleMax: number | null;
  spTrampPressureScaleMin: number | null;
  spTrampPressureStartPressurize: number | null;
  spTrampPressureStopPressurize: number | null;
  spVibAlarmTeethToOpen: number | null;
  spVibCountFeedStoppage: number | null;
  spVibScaleMax: number | null;
  spVibScaleMin: number | null;
  spVibShiftTimeHours: number | null;
  spVibration1Warning: number | null;
  spVibration2Warning: number | null;
  spVibration3Warning: number | null;
  spVibration4Warning: number | null;
  systemControlPower: boolean | null;
  tankLubeHeaterStatus: CrusherRunningEStatus | null;
  tankTempTooLowToRun: boolean | null;
  tankTempTooLowToStart: boolean | null;
  trampPressureSensorFault: boolean | null;
  vibrationSensor1: number | null;
  vibrationSensor2: number | null;
  vibrationSensor3: number | null;
  vibrationSensor4: number | null;
  weatherModeStatus: CrusherWeatherModeStatus | null;

  // *** Setup Properties *** //
  crusherModelPLC: CrusherModel | null;
  crusherMotorQty: boolean | null;
  counterShaftBlowerEnable: boolean | null;
  lubePumpQty: boolean | null;
  tankHeaterQty: boolean | null;
  linerWearEnable: boolean | null;
  feedControlVfdEnable: boolean | null;
  dischargeSuperiorEnable: boolean | null;
  feedSuperiorEnable: boolean | null;
  lubeFlow1Enable: boolean | null;
  lubeFlow2Enable: boolean | null;
  lubeFlowSwitchEnable: boolean | null;
  lubeLevelEnable: boolean | null;
  lubeSupplyTempEnable: boolean | null;
  eccentricBushingTempEnable: boolean | null;
  cavityLevelSensorEnable: boolean | null;
  crusherSpeedMonitorEnable: boolean | null;
  beltSlipMonitorEnable: boolean | null;
  counterShaftBushingTempEnable: boolean | null;
  vibrationSensor1Enable: boolean | null;
  vibrationSensor2Enable: boolean | null;
  vibrationSensor3Enable: boolean | null;
  vibrationSensor4Enable: boolean | null;

  // Alarm
  almCrMotorOverload: AlarmStatus | null;
  almCrCreep: AlarmStatus | null;
  almCrBeltSlipCr: AlarmStatus | null;
  almHydXduCrClampPress: AlarmStatus | null;
  almCrCoastDown: AlarmStatus | null;
  almCrBlowerStarterFault: AlarmStatus | null;
  almLubeFilterDirty: AlarmStatus | null;
  almLubeFilterPlugged: AlarmStatus | null;
  almCrAdjProxFault: AlarmStatus | null;
  almCrAdjSettingFault: AlarmStatus | null;
  almCrSfMotor1: AlarmStatus | null;
  almCrXduCrMotor1Amps: AlarmStatus | null;
  almFeedStarterFault: AlarmStatus | null;
  almHydClampExcessPress: AlarmStatus | null;
  almHydLowTrampPress: AlarmStatus | null;
  almLubeSupplyReturnDifTemp: AlarmStatus | null;
  almHydPumpStarterFault: AlarmStatus | null;
  almHydHighTemp: AlarmStatus | null;
  almHydLevelLow: AlarmStatus | null;
  almHydLowClampPress: AlarmStatus | null;
  almLubeLowLevel: AlarmStatus | null;
  almLubeSfCool1: AlarmStatus | null;
  almLubeSfPump1: AlarmStatus | null;
  almLubeSfHeater: AlarmStatus | null;
  almLubeTankTempHighAlarm: AlarmStatus | null;
  almLubeTankTempHighWarning: AlarmStatus | null;
  almLubeTankTempLowWarning: AlarmStatus | null;
  almLubeTankTempLowAlarm: AlarmStatus | null;
  almLubeXduCrTankTemp: AlarmStatus | null;
  almVibAlm: AlarmStatus | null;
  almVibXduCr1: AlarmStatus | null;
  almVibXduCr2: AlarmStatus | null;
  almVibXduCr3: AlarmStatus | null;
  almVibXduCr4: AlarmStatus | null;
  almLubeSupplyTemp: AlarmStatus | null;
  almLubeXduCrSupplyTemp: AlarmStatus | null;
  almHydXduCrTrampPress: AlarmStatus | null;
  almDCStarterFault: AlarmStatus | null;
  almLubeReturnTempCrusherHighAlarm: AlarmStatus | null;
  almLubeReturnTempCrusherContinuous: AlarmStatus | null;
  almLubeLowLevelCrusherStop: AlarmStatus | null;
  almLubeLowLevelLubeStop: AlarmStatus | null;
  almLubeSupplyReturnDiffCont: AlarmStatus | null;
  almHyd1hourContinuous: AlarmStatus | null;
  almHydMachineStop: AlarmStatus | null;
  almHydTrampExcessPress: AlarmStatus | null;
  almLubeXduCrSupplyPressure: AlarmStatus | null;
  almLubeXduCrreturntemp: AlarmStatus | null;
  almLubeSupplyPressure1: AlarmStatus | null;
  almLubeSupplyPressure2: AlarmStatus | null;
  almCrAdjCssLimits: AlarmStatus | null;
  almCrXduCrSpeed: AlarmStatus | null;
  almLubeReturnTempFeeder: AlarmStatus | null;
  almCrAdjLinerWear: AlarmStatus | null;
  almCrBeltSlipFeed: AlarmStatus | null;
  almVibWarning: AlarmStatus | null;
  almProgEStop: AlarmStatus | null;
  almFltIFmDuplicateAddress: AlarmStatus | null;
  almFltIFmMissingAddress: AlarmStatus | null;
  almFltIFmPeriphery: AlarmStatus | null;
  almFltIFmProfile: AlarmStatus | null;
  almFltIFmProjection: AlarmStatus | null;
  almFltIFmSystem: AlarmStatus | null;
  almCrXduCrMotor2Amps: AlarmStatus | null;
  almLubeFlowSwitchNoFlow: AlarmStatus | null;
  almCrCavityLevelHigh: AlarmStatus | null;
  almCrEccHighTemp: AlarmStatus | null;
  almCrXduCrCavityLevel: AlarmStatus | null;
  almCrXduCrCsbTemp: AlarmStatus | null;
  almCrXduCrEccTemp: AlarmStatus | null;
  almCavityLevelAutoMode: AlarmStatus | null;
  almLubeSfHeaterNumb2: AlarmStatus | null;
  almCrCsbHighTemp: AlarmStatus | null;
  almLubeSuppyPressure2: AlarmStatus | null;
  almCrProx1ZeroSpeed: AlarmStatus | null;
  almCrProx2ZeroSpeed: AlarmStatus | null;
  almAutoVibOpenTimeout: AlarmStatus | null;
  almAutoVibEventCountMax: AlarmStatus | null;
  almAutoCrAmpOpenEventCountMax: AlarmStatus | null;
  almAutoCrAmpOpenTimeout: AlarmStatus | null;
  almAutoCrAmpCloseTimeout: AlarmStatus | null;
  almLubeFlow: AlarmStatus | null;
  almLubeXduCrFlow: AlarmStatus | null;
  almReturnTempForFeeder: AlarmStatus | null;
  almGatewayCommLossCrusher: AlarmStatus | null;
  almGatewayCommLossLube: AlarmStatus | null;
  almAutoStartStep6Fail: AlarmStatus | null;
  almAutoStartStep5Fail: AlarmStatus | null;
  almAutoStartStep4Fail: AlarmStatus | null;
  almAutoStartStep2Fail: AlarmStatus | null;
  almAutoStopStep1Fail: AlarmStatus | null;
  almAutoStopStep2Fail: AlarmStatus | null;
  almAutoStopStep3Fail: AlarmStatus | null;
  almAutoStopStep4Fail: AlarmStatus | null;
}

export interface EquipmentUnitDataVSICrusher {
  // *** EquipmentUnitData *** //
  idEquipmentUnit: string;
  equipmentCategory: EquipmentCategory;
  timestamp: number | null;
  readStatus: EquipmentDataReadStatus;

  // *** GPS Data *** //
  lat?: number | null;
  lng?: number | null;
  gpsTimestamp?: number | null;

  // *** Lid Status *** //
  lastLidOpenedAt?: number | null;

  // *** Alarms *** //
  almBinFeederStarterFault: AlarmStatus | null;
  almCrBeltSlipCr: AlarmStatus | null;
  almCrBeltSlipFeed: AlarmStatus | null;
  almCrCavityLevelHigh: AlarmStatus | null;
  almCrCoastDown: AlarmStatus | null;
  almCrDriveGeneralFault: AlarmStatus | null;
  almCrHighAmp: AlarmStatus | null;
  almCrSfMotor1: AlarmStatus | null;
  almCrSfMotor2: AlarmStatus | null;
  almDischargeStarterFault: AlarmStatus | null;
  almFeedStarterFault: AlarmStatus | null;
  almGatewayCommLossCrusher: AlarmStatus | null;
  almHighVibEvent: AlarmStatus | null;
  almLidOpenCrusherStop: AlarmStatus | null;
  almLidOpenFeederStop: AlarmStatus | null;
  almLubeFlowSwitchNoFlow1: AlarmStatus | null;
  almLubeFlowSwitchNoFlow2: AlarmStatus | null;
  almLubeLowLevel: AlarmStatus | null;
  almLubeLowLevelCrusherStop: AlarmStatus | null;
  almLubeLowLevelLubeStop: AlarmStatus | null;
  almLubeReturnTempCrusherContinuous: AlarmStatus | null;
  almLubeReturnTempCrusherHighAlarm: AlarmStatus | null;
  almLubeReturnTempFeeder: AlarmStatus | null;
  almLubeSfCool1: AlarmStatus | null;
  almLubeSfHeater1: AlarmStatus | null;
  almLubeSfHeater2: AlarmStatus | null;
  almLubeSfPump1: AlarmStatus | null;
  almLubeSfPump2: AlarmStatus | null;
  almLubeTankTemp: AlarmStatus | null;
  almLubeTankTempHighAlarm: AlarmStatus | null;
  almLubeTankTempHighWarning: AlarmStatus | null;
  almLubeTankTempLowAlarm: AlarmStatus | null;
  almLubeXduCrReturnTemp: AlarmStatus | null;
  almLubeXduCrTankTemp: AlarmStatus | null;
  almMetalDetected: AlarmStatus | null;
  almProgEStop: AlarmStatus | null;
  almReturnConveyorDriveFault: AlarmStatus | null;
  almVibWarning: AlarmStatus | null;
  almVibXduCr1: AlarmStatus | null;

  automationType: CrusherAutomationType | null;
  beltSlipMonitorEnable: boolean | null;
  beltSlipPermissive: boolean | null;
  binFeederAutoMode: boolean | null;
  binFeederPermissive: boolean | null;
  binFeederRunHours: number | null;
  binFeederRunningStatus: CrusherRunningSStatus | null;
  binFeederSpeedVFD: number | null;
  coastDownTimeMinutes: number | null;
  coastDownTimeSeconds: number | null;
  coolerFanStatus: CrusherRunningSStatus | null;
  crusherBeltSlip: boolean | null;
  crusherDriveMotorType: CrusherDriveMotorType | null;
  crusherModelPLC: CrusherModel | null;
  crusherMotor1Amps: number | null;
  crusherMotor1Fault: boolean | null;
  crusherMotor1RunHours: number | null;
  crusherMotor2Amps: number | null;
  crusherMotor2Fault: boolean | null;
  crusherMotor2RunHours: number | null;
  crusherMotorOverallStatus: CrusherOverallStatus | null;
  crusherMotorQty: boolean | null;
  crusherMotorRPM: number | null;
  crusherMotorStatus: CrusherRunningSStatus | null;
  crusherSpeedMonitorEnable: boolean | null;
  crusherStartPermissives: boolean | null;
  dischargeRunHours: number | null;
  dischargeRunningStatus: CrusherRunningSStatus | null;
  dischargeSuperiorEnable: boolean | null;
  driveFault: boolean | null;
  driveSpeedHz: number | null;
  enableHMI: boolean | null;
  eStop: boolean | null;
  feedControlVfdEnable: boolean | null;
  feederPermissive: boolean | null;
  feederRunHours: number | null;
  feederRunningStatus: CrusherRunningSStatus | null;
  feederStarterFault: boolean | null;
  feedSuperiorEnable: boolean | null;
  highVibrationPermissive: boolean | null;
  lidLiftCoastLockout: boolean | null;
  lidLimitSwitch: boolean | null;
  lidMovementPermissive: boolean | null;
  lidOpenStatus: boolean | null;
  lowLubeOilGPM1: boolean | null;
  lowLubeOilGPM2: boolean | null;
  lubeHeaterPermissive: boolean | null;
  lubeHeaterStarterFault: boolean | null;
  lubeHighReturnTempPermissive: boolean | null;
  lubeSupplyTempEnable: boolean | null;
  lubeOilLevelLow: boolean | null;
  lubePermissive: boolean | null;
  lubePump1RunHours: number | null;
  lubePump1StarterFault: boolean | null;
  lubePumpRunningStatus: CrusherRunningSStatus | null;
  lubeReturnLowTemp: boolean | null;
  lubeReturnTempHigh: boolean | null;
  lubeReturnTempTransducerFault: boolean | null;
  lubeTankHeaterEnable: boolean | null;
  lubeTankLevel: boolean | null;
  lubeTankOilLevel: boolean | null;
  lubeTankTempTransducerFault: boolean | null;
  lubricationOverallStatus: CrusherOverallStatus | null;
  lubricationReturnTemperature: number | null;
  lubricationSupplyFlowStatus: LubricationFlowStatus | null;
  lubricationSupplyTemperature: number | null;
  lubricationTankLevelStatus: CrusherOverallStatus | null;
  lubricationTankTemperature: number | null;
  mandoorClosed: boolean | null;
  mccLocalMode: boolean | null;
  metalDetector: CrusherMetalDetectorStatus | null;
  metalDetectorEnable: boolean | null;
  minimumLubeRunTimer: boolean | null;
  motorControl: CrusherMotorControl | null;
  spBeltSlipAlarmRpm: number | null;
  spBinFeederHighWarning: number | null;
  spBinFeederWarningHornTime: number | null;
  spCrusherMotorAmpsScaleMax: number | null;
  spCrusherMotorAmpsScaleMin: number | null;
  spCrusherMotorAutoAmps: number | null;
  spCrusherMotorCrushingAmps: number | null;
  spCrusherMotorFullLoadAmps: number | null;
  spCrusherMotorRpmScaleMax: number | null;
  spCrusherMotorRpmScaleMin: number | null;
  spCrusherMotorRunningAmps: number | null;
  spCrusherMotorServiceFactor: number | null;
  spCrusherMotorWarningHornTime: number | null;
  spDischargeWarningHornTime: number | null;
  spFeederWarningHornTime: number | null;
  spLubeCoolerStartTemp: number | null;
  spLubeCoolerStopTemp: number | null;
  spLubeReturnTempCrusherStartPermissive: number | null;
  spLubeReturnTempCrusherStop: number | null;
  spLubeReturnTempHighWarn: number | null;
  spLubeReturnTempLowAlarm: number | null;
  spLubeReturnTempLowWarning: number | null;
  spLubeReturnTempStopFeed: number | null;
  spLubeTankTempHighAlarm: number | null;
  spLubeTankTempHighWarning: number | null;
  spLubeTankTempLowAlarm: number | null;
  spLubeTankTempLowWarning: number | null;
  spReturnTempScaleMax: number | null;
  spReturnTempScaleMin: number | null;
  spSupplyTempScaleMax: number | null;
  spSupplyTempScaleMin: number | null;
  spTankTempScaleMax: number | null;
  spTankTempScaleMin: number | null;
  spTimeAboveFlaFeedStop: number | null;
  spTimeAboveFlaSf115FeedStop: number | null;
  spTimeAboveFlaSfFeedStop: number | null;
  spVFDHzMaxSetpoint: number | null;
  spVFDHzMinSetpoint: number | null;
  spVFDHzScaleMax: number | null;
  spVFDHzScaleMin: number | null;
  spVibration1Alarm: number | null;
  spVibration1DurationAlarm: number | null;
  spVibration1DurationWarning: number | null;
  spVibration1Warning: number | null;
  spVibScaleMax: number | null;
  spVibScaleMin: number | null;
  tankLubeHeaterStatus: CrusherRunningEStatus | null;
  tankTempTooLowToRun: boolean | null;
  tankTempTooLowToStart: boolean | null;
  temperatureConversion: boolean | null;
  unitStatus: CrusherRunningSStatus | null;
  vibrationSensor1: number | null;
  vibrationSensor1Enable: boolean | null;
  vibrationTransducerFault: boolean | null;
  weatherModeStatus: CrusherWeatherModeStatus | null;
}

export type CrusherOperationalData = (
  EquipmentUnitDataConeCrusher | EquipmentUnitDataVSICrusher | EdgeDeviceOperationalStatus
)[];

/* *************************************************************************************************
 **************************************** EXPORT TYPES *********************************************
 ************************************************************************************************ */

/** Represents a single row in the lid stats history table */
export interface LidStatusItem {
  date: number;
  counter: number;
  interval: number | null;
}

/** Response from GET /crusher/lid_stats/{id} */
export interface LidStatusResponse {
  lidStatus: LidStatusItem[];
  totalItems: number;
  page: number;
  pageSize: number;
}
